/**
 * Slice LEVI-CONT-01 · Client-side automatic endpoint detection.
 *
 * A small, additive RMS-based silence detector using the Web Audio
 * API. This does NOT replace transcription (Whisper still transcribes
 * on the server). It only decides "the contractor probably finished
 * this turn" and calls the already-existing stopAndSend() path.
 *
 * Non-goals:
 *   - wake-word activation
 *   - always-on listening
 *   - streaming transcription
 *   - broad latency work
 *   - barge-in / interruption during Levi TTS
 *
 * Behavior:
 *   - Requires at least one voice-frame above a calibrated RMS
 *     threshold before it will fire (empty audio never auto-submits).
 *   - Fires exactly once per session (a re-listen re-arms it).
 *   - Never fires while Levi is speaking (caller must set
 *     isSpeaking()).
 *   - Fail-open: if AudioContext / AnalyserNode is unavailable, this
 *     module becomes a no-op and manual Stop remains authoritative.
 */

// Silence-hang window: after the last voice frame, wait this many
// milliseconds of continuous silence before endpointing the turn.
//
// PO 2026-08-02 correction — raised from 2400 → 4500. Rationale:
// contractor dictation of list-heavy content (customer name →
// address → phone → email, project details, dimensions, dates)
// carries natural recall / reading pauses of 3-5 seconds. The
// previous 2400 ms window ended the turn during those pauses and
// caused Levi to interrupt. 4500 ms covers > 95 % of observed
// natural recall pauses without materially harming short-turn
// responsiveness (a "yes" / "no" / short greeting still endpoints
// ≈ 4.6 s after the last frame — inside the coworker perceived-
// response window because MIN_VOICE_MS + hang > word-length).
//
// PO 2026-02 correction — reduced from 4500 → 3000 ms. Rationale:
// during real conversational use the 4.5 s hang felt too slow.
// 3.0 s is the PO-authorized preview target: still covers ~2.5 s
// natural recall pauses per Boundary Test evidence (A-E dictation
// families all remain ONE contractor turn when pauses stay below
// the endpoint boundary), while removing ~1.5 s of fixed per-turn
// perceived latency. Rollback constant to 4500 ms if
// pause-safety regressions surface during PO real-mic acceptance.
//
// Overridable per-instance via ``silenceHangMs`` on
// ``createLeviVAD``. Overridable at build/deploy time via
// ``REACT_APP_LEVI_VAD_SILENCE_HANG_MS`` (integer milliseconds,
// must be > MIN_VOICE_MS and ≤ SAFETY_MAX_LISTEN_MS to take
// effect; malformed values fall back to the default).
const _SILENCE_HANG_MS_ENV = (() => {
  try {
    const raw = (typeof process !== "undefined"
                 && process.env
                 && process.env.REACT_APP_LEVI_VAD_SILENCE_HANG_MS);
    if (!raw) return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    if (n < 600 || n > 45000) return null;   // sanity bounds
    return Math.floor(n);
  } catch { return null; }
})();
const SILENCE_HANG_MS_DEFAULT = _SILENCE_HANG_MS_ENV != null
  ? _SILENCE_HANG_MS_ENV
  : 3000;

// Exported for tests and diagnostic telemetry only. Do not use to
// mutate behavior — pass ``silenceHangMs`` to ``createLeviVAD``.
export const LEVI_VAD_DEFAULTS = Object.freeze({
  SILENCE_HANG_MS: SILENCE_HANG_MS_DEFAULT,
  RMS_VOICE_THRESHOLD: 0.035,
  SAFETY_MAX_LISTEN_MS: 120000,
  // C-3 · exported for tests. Kept in sync with the private const
  // below (single source of truth).
  NOVOICE_GRACE_MS: 8000,
  MIN_VOICE_MS: 600,
  SILENCE_HANG_MS_ENV_APPLIED: _SILENCE_HANG_MS_ENV != null,
});

const RMS_VOICE_THRESHOLD_DEFAULT = 0.035;   // ~-29 dBFS on typical mic
const SAFETY_MAX_LISTEN_MS = 120000;          // P0 stabilization
// 2026-08-09 — raised from 45 000 ms so a legitimate contractor
// narration of up to two minutes is not silently truncated. The
// 8-second `NOVOICE_GRACE_MS` still ends listening on silence-hang;
// this constant is the ABSOLUTE recorder-safety watchdog against a
// runaway open microphone, not a normal turn boundary.
// C-3 (PO 2026-08-06) · Active post-reply microphone listening
// window. PO authorization 2026-08-09 SETS this to 8000 ms as part
// of the Vision post-walk correction bounded scope. The prior
// setting (6000 ms, PO 2026-08-07) is superseded by this bounded
// change; no other Voice behavior is altered. Preserves natural
// coworker-style turn-taking while limiting unattended active-
// microphone capture to approximately 8 seconds. Explicitly REJECTS
// the 15 s and 30 s alternatives. This is the ONLY active-
// microphone no-voice cutoff in the frontend —
// `CONVERSATION_INACTIVITY_MS` (30 s) is the broader conversation
// loop ceiling and `LISTEN_HARD_CEILING_MS` (60 s) is the hard
// recorder-safety watchdog; neither is modified.
const NOVOICE_GRACE_MS = 8000;
const MIN_VOICE_MS = 600;                    // minimum sustained voice before endpoint arms

export function createLeviVAD({
  stream,
  onEndpoint,
  onVoiceStart,
  isSpeaking,
  rmsThreshold = RMS_VOICE_THRESHOLD_DEFAULT,
  silenceHangMs = SILENCE_HANG_MS_DEFAULT,
  // PO 2026-08-29 · P0 Voice bounded repair. Optional diagnostic
  // sink injected by the caller (useLeviCommand.js already imports
  // pushDiag from leviSpeech). No-op when absent so this module
  // remains free of cross-module imports.
  pushDiag,
} = {}) {
  if (!stream || typeof onEndpoint !== "function") {
    return { stop: () => {}, dispose: () => {} };
  }
  const AudioCtor = (typeof window !== "undefined")
    ? (window.AudioContext || window.webkitAudioContext)
    : null;
  if (!AudioCtor) {
    return { stop: () => {}, dispose: () => {} };
  }

  // PO 2026-08-29 · P0 Voice bounded repair. Safe diagnostic emit
  // helper — swallows every failure so a diagnostic issue can
  // never break the VAD tick loop.
  const _diag = (evt, payload) => {
    try {
      if (typeof pushDiag === "function") pushDiag(evt, payload || {});
    } catch { /* diagnostics must never throw */ }
  };
  // PO 2026-08-29 · P0 Voice bounded repair. Cache the primary audio
  // track so tick can surface readyState / muted in the periodic
  // rms_snapshot without repeated getAudioTracks() churn. Held by
  // reference; when the caller stops the track (release_mic), reads
  // will still succeed and simply reflect the ended state.
  let audioTrack = null;
  try {
    const _tracks = (stream.getAudioTracks && stream.getAudioTracks()) || [];
    audioTrack = _tracks[0] || null;
  } catch { audioTrack = null; }

  let audioCtx;
  let source;
  let analyser;
  let buf;
  let raf = 0;
  let firedRef = false;
  let voiceEverRef = false;
  let lastVoiceAt = 0;
  // Accumulated sustained voice in ms — a single frame above threshold
  // is not enough to arm the endpoint. Only when the contractor has
  // been meaningfully speaking for MIN_VOICE_MS do we treat this as a
  // real turn.
  let voiceMsRef = 0;
  let lastFrameAt = 0;
  const startedAt = performance.now();
  // P0 (PO 2026-08-09) · exposed diagnostic + waveform snapshot state.
  // Any external consumer (LeviListeningVisualizer) reads through the
  // returned accessors so we NEVER create a second WebAudio observation
  // branch on the same MediaStream — a duplicate branch starves this
  // analyser on iOS Safari and caused a false silence_after_voice
  // endpoint during long continuous narration.
  let lastRms = 0;
  let silenceStartedAt = 0;
  // PO 2026-08-29 · P0 Voice bounded repair. Consecutive non-running
  // audioCtx ticks. Two in a row triggers the audio_context_stalled
  // endpoint so a stalled context cannot masquerade as ordinary
  // contractor silence for the full 8-second NOVOICE_GRACE_MS.
  let stalledTickCount = 0;
  // Throttled rms_snapshot emission (~1 s) so field diagnostics
  // capture rms / audioCtx.state / track health without flooding the
  // event ring.
  let lastRmsSnapshotAt = 0;
  const RMS_SNAPSHOT_INTERVAL_MS = 1000;

  try {
    audioCtx = new AudioCtor();
    analyser = audioCtx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.6;
    source = audioCtx.createMediaStreamSource(stream);
    source.connect(analyser);
    buf = new Uint8Array(analyser.fftSize);
  } catch {
    return { stop: () => {}, dispose: () => {} };
  }

  // PO 2026-08-29 · P0 Voice bounded repair (Correction 1).
  // Chromium's autoplay policy suspends any AudioContext created
  // outside a fresh user gesture — including the setTimeout(200 ms)
  // auto-arm inside `_onSpokenEnd` that follows a Levi TTS reply.
  // A suspended AudioContext's analyser returns a flat midpoint
  // buffer, producing RMS ≈ 0 and a false `no_voice_detected` after
  // 8 s while the contractor is actively speaking. Invoke resume()
  // here so the context is running before the first tick. Idempotent
  // no-op when the state is already "running".
  try {
    _diag("vad:audioctx_created", { state: audioCtx.state || null });
    if (audioCtx.state === "suspended" && typeof audioCtx.resume === "function") {
      const p = audioCtx.resume();
      if (p && typeof p.then === "function") {
        p.then(() => {
          _diag("vad:audioctx_resumed", { state: audioCtx.state || null, path: "async" });
        }).catch((err) => {
          _diag("vad:audioctx_resume_failed", { error: String((err && err.message) || err) });
        });
      } else {
        _diag("vad:audioctx_resumed", { state: audioCtx.state || null, path: "sync" });
      }
    }
  } catch (err) {
    _diag("vad:audioctx_resume_failed", { error: String((err && err.message) || err) });
  }

  const fire = (reason) => {
    if (firedRef) return;
    firedRef = true;
    try { onEndpoint({ reason, voiceEver: voiceEverRef }); }
    catch { /* onEndpoint errors are the caller's problem */ }
  };

  const tick = () => {
    if (firedRef) return;
    // PO 2026-08-29 · P0 Voice bounded repair (Correction 2).
    // Stalled-AudioContext detection. If the AudioContext is not
    // running, `getByteTimeDomainData` returns a flat midpoint buffer
    // (RMS ≈ 0) which the sustained-voice branch below can never
    // arm, and the loop would silently time out at NOVOICE_GRACE_MS
    // as `no_voice_detected` — the exact failure the PO diagnostic
    // captured. Two consecutive non-running ticks endpoint the loop
    // via the existing onEndpoint contract with a distinct reason so
    // the caller can hand control back to a fresh user-gesture retry
    // (Tap-to-Speak) instead of the silent 8-second dead-end.
    const _ctxState = (audioCtx && audioCtx.state) || null;
    if (_ctxState && _ctxState !== "running") {
      stalledTickCount += 1;
      if (stalledTickCount >= 2) {
        _diag("vad:audioctx_stalled", {
          state: _ctxState,
          consecutive_ticks: stalledTickCount,
          elapsed_ms: Math.round(performance.now() - startedAt),
          voice_ever: voiceEverRef,
        });
        fire("audio_context_stalled");
        return;
      }
      raf = requestAnimationFrame(tick);
      return;
    }
    if (stalledTickCount > 0) stalledTickCount = 0;
    // Levi speaking → do not endpoint. This defends against the
    // recorder still running during Levi TTS.
    const speaking = (typeof isSpeaking === "function")
      ? Boolean(isSpeaking())
      : false;
    try { analyser.getByteTimeDomainData(buf); }
    catch { return; }
    // Compute RMS on [-1, 1] deviation from 128 midpoint.
    let sum = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = (buf[i] - 128) / 128;
      sum += v * v;
    }
    const rms = Math.sqrt(sum / buf.length);
    lastRms = rms;
    const now = performance.now();
    // Accumulate sustained voice-milliseconds across frames. Sustained
    // voice is what arms the endpoint — a lone RMS spike from a door
    // slam or keyboard click does not.
    const dt = lastFrameAt ? (now - lastFrameAt) : 0;
    lastFrameAt = now;
    if (!speaking && rms >= rmsThreshold) {
      voiceMsRef += dt;
      silenceStartedAt = 0;
      if (voiceMsRef >= MIN_VOICE_MS) {
        if (!voiceEverRef) {
          // P0 RCA 2026-08-09 · fire voice-start exactly once. The
          // caller uses this to CANCEL / RESET the outer 30-second
          // conversation inactivity ceiling so it never fires mid-
          // utterance. Sustained voice — not a single-frame spike —
          // is the trigger, matching the endpoint arming criterion.
          voiceEverRef = true;
          try {
            if (typeof onVoiceStart === "function") onVoiceStart({ atMs: now });
          } catch { /* onVoiceStart errors are the caller's problem */ }
        }
      }
      lastVoiceAt = now;
    } else if (voiceEverRef && !speaking) {
      // Track when the current silence stretch began (for
      // diagnostic emission — behaviorally identical to prior code
      // which only tracked lastVoiceAt).
      if (!silenceStartedAt) silenceStartedAt = now;
    }
    // Endpoint conditions:
    //   1. we heard SUSTAINED voice (>= MIN_VOICE_MS), then silence
    //      for >= silenceHangMs → done.
    //   2. never any sustained voice within NOVOICE_GRACE_MS → stop
    //      the mic quietly (empty-audio branch in the caller).
    //   3. safety ceiling: HARD cap regardless of silence-hang. In
    //      noisy rooms ambient RMS keeps updating ``lastVoiceAt`` and
    //      the silence-hang window never accumulates. Once we cross
    //      ``SAFETY_MAX_LISTEN_MS`` we endpoint unconditionally so
    //      the mic actually releases. See PO recorder-lifecycle
    //      report 2026-08-04.
    if (voiceEverRef && !speaking
        && now - lastVoiceAt >= silenceHangMs) {
      fire("silence_after_voice");
      return;
    }
    if (!voiceEverRef && now - startedAt >= NOVOICE_GRACE_MS) {
      fire("no_voice_detected");
      return;
    }
    if (now - startedAt >= SAFETY_MAX_LISTEN_MS) {
      // Hard cap. Fire regardless of whether the caller ever heard
      // sustained voice — if we've been listening for 45s straight
      // without endpointing naturally, something is off (ambient
      // noise, mic left open, contractor walked away). Endpointing
      // here restores the UI to idle. ``voiceEverRef`` is preserved
      // so the caller can distinguish "long turn with real speech"
      // from "no speech at all" (empty-chunk path).
      fire("safety_ceiling");
      return;
    }
    // PO 2026-08-29 · P0 Voice bounded repair (Correction 3).
    // Throttled rms_snapshot so field diagnostics can distinguish
    // H1 (suspended audioCtx), H2 (device conflict), H3 (live-but-
    // silent track) after any future reproduction. Emits at most
    // once per RMS_SNAPSHOT_INTERVAL_MS. Never logs raw microphone
    // audio, never logs customer data — only the state needed to
    // classify the failure class.
    if (now - lastRmsSnapshotAt >= RMS_SNAPSHOT_INTERVAL_MS) {
      lastRmsSnapshotAt = now;
      _diag("vad:rms_snapshot", {
        current_rms: Number.isFinite(rms) ? Number(rms.toFixed(5)) : null,
        threshold: rmsThreshold,
        audio_ctx_state: _ctxState,
        track_muted: audioTrack ? !!audioTrack.muted : null,
        track_ready_state: audioTrack ? audioTrack.readyState : null,
        voice_ever: voiceEverRef,
        elapsed_ms: Math.round(now - startedAt),
      });
    }
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);

  const dispose = () => {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    try { source && source.disconnect(); } catch { /* noop */ }
    try { analyser && analyser.disconnect(); } catch { /* noop */ }
    try { audioCtx && audioCtx.close(); } catch { /* noop */ }
  };
  const stop = () => { firedRef = true; dispose(); };

  // P0 (PO 2026-08-09) · Read-only accessors for external consumers.
  //
  // getWaveformSnapshot()  — copies the current time-domain byte
  //   buffer into a caller-provided Uint8Array (or returns a fresh
  //   copy). Never re-creates a MediaStreamAudioSourceNode.
  //
  // getDiagnostics()       — { current_rms, threshold, last_voice_at,
  //   silence_started_at, silence_duration_ms, audio_ctx_state }.
  //
  // These exist so LeviListeningVisualizer can render its waveform +
  // publish keep-alive diagnostics WITHOUT creating a second WebAudio
  // observation branch on the same MediaStream. A duplicate branch
  // starves this analyser on iOS Safari (verified 2026-08-09) and
  // caused a false silence_after_voice endpoint mid-narration.
  const getWaveformSnapshot = (out) => {
    try {
      if (firedRef || !analyser || !buf) return null;
      const n = buf.length;
      const target = (out && out.length === n) ? out : new Uint8Array(n);
      analyser.getByteTimeDomainData(target);
      return target;
    } catch { return null; }
  };
  const getAnalyserFftSize = () => (analyser && analyser.fftSize) || 0;
  const getDiagnostics = () => {
    try {
      const now = performance.now();
      const silence_duration_ms = (silenceStartedAt && !voiceEverRef)
        ? 0
        : (silenceStartedAt ? Math.max(0, now - silenceStartedAt) : 0);
      return {
        current_rms: Number.isFinite(lastRms) ? lastRms : null,
        threshold: rmsThreshold,
        last_voice_at: lastVoiceAt || null,
        silence_started_at: silenceStartedAt || null,
        silence_duration_ms,
        audio_ctx_state: (audioCtx && audioCtx.state) || null,
        voice_ever: voiceEverRef,
        fired: firedRef,
      };
    } catch { return null; }
  };

  return { stop, dispose, getWaveformSnapshot, getAnalyserFftSize, getDiagnostics };
}
